import React, { useEffect, useRef, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Card } from '@braze/web-sdk'
import md5 from 'md5'
import { SideFlyoutContainer, Icons } from 'msp-components'
import { useBraze } from 'msp-integrations'
import { NotificationFlyoutProps } from './NotificationFlyout.types'

const NotificationFlyout = ({
  className,
  isOpen,
  onOpen,
  onClose
}: NotificationFlyoutProps) => {
  const { formatMessage } = useIntl()
  const {
    showContentCards,
    hideContentCards,
    notificationQuantity,
    updateUnreadNotificationCount,
    termsConditionsCards,
    notificationCards
  } = useBraze()

  const filterNotifications = (cards: Card[]) => {
    return cards.filter((card) => card.extras?.type === 'notification')
  }

  const initializeBrazeNotificationArea = () => {
    const notificationArea = document.getElementById(
      'content-area-notifications'
    )
    if (notificationArea) {
      showContentCards(notificationArea, filterNotifications)
    }
  }

  const observerRef = useRef<MutationObserver | null>(null)

  const buildAccordionsForTerms = (
    id: string,
    title: string,
    description: string
  ) => {
    return `
      <div class="border-t-[1px] border-t-mediumGray" id="${id}">
        <button
          type="button"
          class="w-full flex justify-between items-center px-6 py-8 text-left transition group accordion-toggle"
          data-target="description-${id}"
          data-arrow="arrow-${id}"
        >
          <span class="font-medium uppercase text-12">${title}</span>
          <svg id="arrow-${id}" class="w-5 h-5 ml-2 transition-transform duration-300" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div id="description-${id}" class="px-6 text-14 leading-20 pb-4 text-mediumGray" style="display:none;">
          ${description}
        </div>
      </div>
    `
  }

  const injectTermsIntoCards = useCallback(() => {
    const notificationArea = document.getElementById(
      'content-area-notifications'
    )
    if (!notificationArea) return
    termsConditionsCards.forEach((term: Card, key: string) => {
      const related = notificationCards.filter(
        (n: Card) => n.extras?.terms_id === key
      )
      related.forEach((n: Card) => {
        const el = notificationArea.querySelector(`[data-ab-card-id='${n.id}']`)
        if (!el) return
        const hash = md5(n.id || '')
        const accordionId = `${key}_${hash}`
        if (el.querySelector(`#${accordionId}`)) return
        const html = buildAccordionsForTerms(
          accordionId,
          term.title || '',
          term.description || ''
        )
        const wrapper = document.createElement('div')
        wrapper.innerHTML = html
        el.appendChild(wrapper)
      })
    })
  }, [termsConditionsCards, notificationCards])

  const startObserver = useCallback(() => {
    const area = document.getElementById('content-area-notifications')
    if (!area) return
    if (observerRef.current) observerRef.current.disconnect()
    const obs = new MutationObserver((mutations) => {
      let shouldReinject = false
      mutations.forEach((m) => {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          shouldReinject = true
        }
      })
      if (shouldReinject) {
        requestAnimationFrame(() => injectTermsIntoCards())
      }
    })
    obs.observe(area, { childList: true, subtree: true })
    observerRef.current = obs
    injectTermsIntoCards()
  }, [injectTermsIntoCards])

  const handleAccordionToggle = (e: Event) => {
    const target = e.target as HTMLElement
    const button = target.closest('.accordion-toggle') as HTMLButtonElement
    if (!button) return
    const contentId = button.dataset.target
    const arrowId = button.dataset.arrow
    if (!contentId || !arrowId) return
    const content = document.getElementById(contentId)
    const arrow = document.getElementById(arrowId)
    if (!content || !arrow) return
    const open = content.style.display === 'block'
    if (open) {
      content.style.display = 'none'
      arrow.style.transform = ''
    } else {
      content.style.display = 'block'
      arrow.style.transform = 'rotate(180deg)'
    }
  }

  const handleOpen = () => {
    initializeBrazeNotificationArea()
    startObserver()
    const area = document.getElementById('content-area-notifications')
    area?.addEventListener('click', handleAccordionToggle)
    onOpen && onOpen()
  }

  const handleClose = () => {
    updateUnreadNotificationCount()
    observerRef.current?.disconnect()
    observerRef.current = null
    const area = document.getElementById('content-area-notifications')
    area?.removeEventListener('click', handleAccordionToggle)
    hideContentCards()
    onClose && onClose()
  }

  // Re-inject when tab becomes visible again (Braze may have re-rendered)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && isOpen) {
        injectTermsIntoCards()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [injectTermsIntoCards, isOpen])

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
      const area = document.getElementById('content-area-notifications')
      area?.removeEventListener('click', handleAccordionToggle)
    }
  }, [])

  return (
    <SideFlyoutContainer
      className={className}
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      position='right'
      titleContent={
        <div className='flex flex-row items-center space-x-1 whitespace-nowrap stroke-black stroke-1'>
          <div>
            {formatMessage({
              id: 'msp.notifications_flyout.title',
              defaultMessage: 'Notifications'
            })}
          </div>
          <Icons iconName={'BellEmpty'} height={'13px'} width={'13px'} />
          <div>{`(${notificationQuantity})`}</div>
        </div>
      }
    >
      <div className='min-h-1 w-full bg-black'></div>
      <div
        className='bg-lightGray h-full w-auto overflow-auto'
        id='content-area-notifications'
      />
    </SideFlyoutContainer>
  )
}

export default NotificationFlyout
